import React, { FunctionComponent, useContext, useState } from "react";
import { UserContext } from "../../UserProvider";
import { MarketingType, State } from "./types";
import useStyles from "./styles";
import { Authenticated, hasRole } from "../../models/User";
import { permissions } from "../../constants";
import Alert from "@material-ui/lab/Alert";
import { AlertTitle } from "@material-ui/lab";
import { StoreSelector } from "../../components/store-selector/StoreSelector";
import { SegmentSelect } from "./components/SegmentSelect";
import { Campaign } from "../../models/Campaign";
import {
  Button,
  Card,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { EmailTemplateSelect } from "./components/EmailTemplateSelect";
import {
  CreateEmailTemplate,
  EmailTemplate,
  targetGroupToDisplayName
} from "../../models/Marketing";
import { CreateEmailTemplateDialog } from "./components/CreateEmailTemplateDialog";
import * as MarketingApi from "../../api/marketingApi";
import { isOk } from "../../result";
import { ConfirmDialog } from "../../components/dialogs/ConfirmDialog";
import { useSelector } from "react-redux";
import store, { AppState } from "../../store";
import { useLoadIfEmpty } from "../../hooks/useLoadIfEmpty";
import { thunkFetchCurrentTenantLanguages } from "../../store/tenants/actions";
import {
  MultilingualNotificationMessage,
  NotificationMessage
} from "../../models/Notification";
import { Segment } from "../../models/Segment";
import { StoresState } from "../../store/stores/types";
import { Language } from "../../models/Translation";
import { useI18n } from "../../hooks/useI18n";

export const Marketing: FunctionComponent = () => {
  const classes = useStyles();
  const user = useContext(UserContext);
  const [state, setState] = useState<State>(
    user.type === "ANONYMOUS" || !hasRole(user, permissions.marketing)
      ? {
          type: "NOT_ALLOWED"
        }
      : {
          type: "MANAGE"
        }
  );

  const tenantLanguages =
    useSelector<AppState, ReadonlyArray<Language>>(
      s => s.currentTenant.languages
    ) || [];

  useLoadIfEmpty(!tenantLanguages.length, thunkFetchCurrentTenantLanguages);

  const storesState = useSelector<AppState, StoresState>(s => s.stores);
  let { stores } = storesState;

  const notificationTitleMaxLength = 45;
  const notificationBodyMaxLength = 160;

  const { t } = useI18n();

  switch (state.type) {
    case "UNEXPECTED_ERROR":
      return (
        <>
          <Alert severity="error" className={classes.trailing}>
            <AlertTitle>
              {state.problem.status}, {state.problem.title}
            </AlertTitle>
            {state.problem.detail} <strong>{state.problem.traceId}</strong>
          </Alert>
          <Button
            variant={"contained"}
            color={"primary"}
            onClick={() => setState({ type: "MANAGE" })}
          >
            {t("common.actions.refresh")}
          </Button>
        </>
      );

    case "NOT_ALLOWED":
      return (
        <>
          <Alert severity="warning">
            {t("common.messages.notAllowedToSeeThisView")}
          </Alert>
        </>
      );

    case "MANAGE":
      const getStoreNames = (): string => {
        if (!state.storeIds || state.storeIds.length <= 0) return "";

        let names = "";

        state.storeIds.forEach((storeId: string) => {
          if (names !== "") {
            names += ", ";
          }
          names += stores.find(s => s.id === storeId)?.name;
        });

        return names;
      };

      const isTargetGroupValid = (): boolean => {
        if (!state.targetGroup) {
          return false;
        }

        switch (state.targetGroup) {
          case "RecipientsInStore":
            return !!(state.storeIds && state.storeIds.length > 0);

          case "UsersInASegment":
            return state.segment !== undefined;

          default:
            return true;
        }
      };

      const getPushNotificationMessagesOrDefault = (
        notification: MultilingualNotificationMessage | undefined
      ) =>
        !notification || Object.keys(notification.messages).length === 0
          ? ({} as { [key: string]: NotificationMessage })
          : notification.messages;

      const onMarketingTypeSelected = (marketingType: MarketingType) => {
        let notification: MultilingualNotificationMessage | undefined =
          undefined;

        if (
          marketingType === "notification" &&
          state.targetGroup === "RecipientsOfCampaign"
        ) {
          notification = {
            messages: {} as { [key: string]: NotificationMessage }
          };

          for (let x of tenantLanguages) {
            notification.messages[x.code] = { route: "Campaign" };
          }
        }

        return setState({
          ...state,
          marketingType,
          pushNotification: notification
        });
      };

      const onEmailTemplateSelected = (emailTemplate: EmailTemplate) => {
        return setState({ ...state, emailTemplate });
      };

      const showCreateEmailTemplateDialog = () => {
        return setState({ ...state, creatingEmailTemplate: true });
      };

      const closeCreateEmailTemplateDialog = () => {
        return setState({ ...state, creatingEmailTemplate: undefined });
      };

      const confirmCreateEmailTemplateDialog = (model: CreateEmailTemplate) => {
        setState({ ...state, savingEmailTemplate: true });

        MarketingApi.createEmailTemplate(model).then(result => {
          if (isOk(result)) {
            return setState({
              ...state,
              emailTemplate: result.value,
              creatingEmailTemplate: undefined,
              savingEmailTemplate: undefined
            });
          }

          return setState({
            type: "UNEXPECTED_ERROR",
            problem: result.details
          });
        });
      };

      const showSendEmailsConfirmationDialog = () => {
        return setState({ ...state, confirmingSendEmails: true });
      };

      const closeSendEmailsConfirmationDialog = () => {
        return setState({ ...state, confirmingSendEmails: undefined });
      };

      const confirmSendEmailsConfirmationDialog = (): boolean => {
        if (!state.targetGroup || !state.emailTemplate) return false;

        setState({ type: "SENDING" });

        MarketingApi.sendEmailToTargetGroup({
          targetGroup: state.targetGroup,
          emailTemplate: state.emailTemplate,
          emailTemplateId: state.emailTemplate.id,
          storeIds: state.storeIds ?? [],
          campaignId: state.campaign?.id,
          segmentId: state.segment?.id
        }).then(result => {
          if (isOk(result)) {
            return setState({ type: "EMAILS_SENT" });
          }

          return setState({
            type: "UNEXPECTED_ERROR",
            problem: result.details
          });
        });

        return true;
      };

      const showSendEmailPreviewDialog = () => {
        let authenticated = user as Authenticated;
        return setState({
          ...state,
          confirmingEmailPreview: true,
          previewEmailAddress: authenticated?.email
        });
      };

      const closeSendEmailPreviewDialog = () => {
        return setState({
          ...state,
          confirmingEmailPreview: undefined,
          sendingEmailPreview: undefined,
          previewEmailAddress: undefined
        });
      };

      const confirmSendEmailPreviewDialog = (): boolean => {
        if (
          !state.targetGroup ||
          !state.emailTemplate ||
          !state.previewEmailAddress
        )
          return false;

        setState({ ...state, sendingEmailPreview: true });

        MarketingApi.sendEmailPreview({
          emailTemplateId: state.emailTemplate.id,
          userEmail: state.previewEmailAddress
        }).then(result => {
          if (!isOk(result)) {
            console.error(result.details);
          }

          return setState({
            ...state,
            confirmingEmailPreview: undefined,
            sendingEmailPreview: undefined,
            previewEmailAddress: undefined
          });
        });

        setTimeout(() => {
          setState({
            ...state,
            confirmingEmailPreview: undefined,
            sendingEmailPreview: undefined,
            previewEmailAddress: undefined
          });
        }, 3000);

        return false;
      };

      const isNotificationsValid = (): boolean => {
        for (let x of tenantLanguages) {
          let message =
            (state.pushNotification &&
              state.pushNotification.messages[x.code]) ??
            null;

          if (message === null || !message.title || message.title.length > 45)
            return false;

          if (!message.body || message.body.length > 160) return false;
        }

        return true;
      };

      const showSendNotificationsConfirmationDialog = () => {
        if (!isNotificationsValid()) {
          return;
        }

        return setState({ ...state, confirmingSendNotifications: true });
      };

      const closeSendNotificationsConfirmationDialog = () => {
        return setState({ ...state, confirmingSendNotifications: undefined });
      };

      const confirmSendNotificationsConfirmationDialog = (): boolean => {
        if (
          !state.targetGroup ||
          !state.pushNotification ||
          !isNotificationsValid()
        )
          return false;

        setState({ type: "SENDING" });

        MarketingApi.sendPushNotificationToTargetGroup({
          targetGroup: state.targetGroup,
          storeIds: state.storeIds ?? [],
          campaignId: state.campaign?.id,
          segmentId: state.segment?.id,
          messages: state.pushNotification.messages
        }).then(result => {
          if (isOk(result)) {
            return setState({ type: "NOTIFICATIONS_SENT" });
          }

          return setState({
            type: "UNEXPECTED_ERROR",
            problem: result.details
          });
        });

        return true;
      };

      return (
        <>
          <Card className={classes.section}>
            <FormControl
              required
              variant={"standard"}
              className={classes.field}
            >
              <InputLabel id="recipient-type-select-label">
                Select recipients
              </InputLabel>
              <Select
                required
                id="recipient-type-select"
                labelId="recipient-type-select-label"
                value={state.targetGroup}
                onChange={e => {
                  if (!e.target.value) return;
                  setState({
                    ...state,
                    targetGroup: e.target.value as string,
                    showStoreSelect: stores.length > 1,
                    storeIds:
                      stores.length > 1 ? undefined : stores.map(s => s.id),
                    marketingType: !hasRole(user, permissions.root)
                      ? "notification"
                      : undefined
                  });
                }}
                autoWidth={true}
              >
                {stores && stores.length === 1 ? (
                  <MenuItem
                    key={"customersOfAStore"}
                    value={"CustomersOfAStore"}
                  >
                    <ListItemText
                      primary={t(
                        "common.marketing.customersThatHaveVisitedMyStore"
                      )}
                    />
                  </MenuItem>
                ) : (
                  <MenuItem
                    key={"CustomersOfAStore"}
                    value={"CustomersOfAStore"}
                  >
                    <ListItemText
                      primary={t("common.marketing.customersOfAStore")}
                    />
                  </MenuItem>
                )}

                <MenuItem key={"UsersInASegment"} value={"UsersInASegment"}>
                  <ListItemText
                    primary={t("common.marketing.usersInASegment")}
                  />
                </MenuItem>
              </Select>
            </FormControl>

            {state.targetGroup === "CustomersOfAStore" &&
              state.showStoreSelect && (
                <StoreSelector
                  selectedStoreIds={state.storeIds?.slice() ?? []}
                  onSelectedStore={storeIds => {
                    return setState({ ...state, storeIds });
                  }}
                />
              )}

            {state.targetGroup === "UsersInASegment" && (
              <SegmentSelect
                required
                className={classes.field}
                value={state.segment?.id}
                onSelected={segment => {
                  setState({ ...state, segment });
                }}
              />
            )}
          </Card>
          {isTargetGroupValid() && (
            <>
              <Card className={classes.section} style={{ marginBottom: 20 }}>
                {hasRole(user, permissions.root) && (
                  <FormControl
                    required
                    variant={"standard"}
                    className={classes.field}
                  >
                    <InputLabel id="marketing-type-select-label">
                      {t("common.marketing.marketingType")}
                    </InputLabel>
                    <Select
                      required
                      id="marketing-type-select"
                      labelId="marketing-type-select-label"
                      value={state.marketingType ?? ""}
                      onChange={e =>
                        onMarketingTypeSelected(e.target.value as MarketingType)
                      }
                      autoWidth={true}
                    >
                      <MenuItem key={"email"} value={"email"}>
                        <ListItemText primary={t("common.marketing.email")} />
                      </MenuItem>

                      <MenuItem key={"notification"} value={"notification"}>
                        <ListItemText
                          primary={t("common.messages.pushNotification")}
                        />
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}

                {state.marketingType === "email" && (
                  <>
                    <EmailTemplateSelect
                      required
                      className={classes.field}
                      value={state.emailTemplate?.id}
                      onSelected={onEmailTemplateSelected}
                      updatedSinceDaysBack={30}
                      additionalTemplates={
                        state.emailTemplate ? [state.emailTemplate] : []
                      }
                    />
                    <Button
                      className={classes.field}
                      variant={"contained"}
                      color={"primary"}
                      onClick={showCreateEmailTemplateDialog}
                    >
                      Create new template
                    </Button>

                    <CreateEmailTemplateDialog
                      visible={
                        state.creatingEmailTemplate !== undefined &&
                        state.creatingEmailTemplate
                      }
                      disabled={
                        state.savingEmailTemplate !== undefined &&
                        state.savingEmailTemplate
                      }
                      onCancel={closeCreateEmailTemplateDialog}
                      onConfirm={confirmCreateEmailTemplateDialog}
                    />

                    {state.emailTemplate && (
                      <Alert severity="info">
                        <AlertTitle>
                          {t("common.messages.availableTags")}
                        </AlertTitle>
                        The following dynamic tags are injected into the
                        template: <b>{"{{firstName}}"}</b>,{" "}
                        <b>{"{{lastName}}"}</b>,{" "}
                        <b>{"{{senderCompanyName}}"}</b>,{" "}
                        <b>{"{{senderAddress}}"}</b>, <b>{"{{senderZip}}"}</b>,{" "}
                        <b>{"{{senderCity}}"}</b>.
                      </Alert>
                    )}
                  </>
                )}

                {state.marketingType === "notification" && (
                  <>
                    <Typography variant="h5">
                      Push notification content
                    </Typography>
                    <Typography>
                      Here you add the title and body text. Remember to add for
                      all the languages since you might have customers speaking
                      different languages
                    </Typography>
                    {tenantLanguages &&
                      tenantLanguages.map(l => (
                        <React.Fragment key={l?.code}>
                          <TextField
                            required
                            className={classes.field}
                            label={`Title (${l.name})`}
                            type="text"
                            value={
                              state.pushNotification?.messages[l.code]
                                ? state.pushNotification?.messages[l.code]
                                    ?.title
                                : ""
                            }
                            onChange={e => {
                              let messages =
                                getPushNotificationMessagesOrDefault(
                                  state.pushNotification
                                );
                              messages[l.code] = {
                                ...messages[l.code],
                                title: e.target.value as string
                              };
                              setState({
                                ...state,
                                pushNotification: {
                                  ...state.pushNotification,
                                  messages: messages
                                }
                              });
                            }}
                            helperText={`Max ${notificationTitleMaxLength} characters`}
                            inputProps={{
                              maxLength: notificationTitleMaxLength
                            }}
                          />
                          <TextField
                            required
                            className={classes.field}
                            label={`Body (${l.name})`}
                            type="text"
                            value={
                              state.pushNotification?.messages[l.code]
                                ? state.pushNotification?.messages[l.code].body
                                : ""
                            }
                            onChange={e => {
                              let messages =
                                getPushNotificationMessagesOrDefault(
                                  state.pushNotification
                                );
                              messages[l.code] = {
                                ...messages[l.code],
                                body: e.target.value as string
                              };
                              setState({
                                ...state,
                                pushNotification: {
                                  ...state.pushNotification,
                                  messages: messages
                                }
                              });
                            }}
                            multiline
                            helperText={`Max ${notificationBodyMaxLength} characters`}
                            inputProps={{
                              maxLength: notificationBodyMaxLength
                            }}
                          />
                        </React.Fragment>
                      ))}
                  </>
                )}
              </Card>

              {state.marketingType === "email" && state.emailTemplate && (
                <Card className={classes.section}>
                  <Alert severity="warning" className={classes.trailing}>
                    <AlertTitle>{t("common.messages.dontForget")}</AlertTitle>
                    Make sure the tags in the template are correct before
                    sending. Easiest way is sending a preview to yourself before
                    sending to all users.
                  </Alert>
                  <Button
                    style={{ marginRight: "1rem" }}
                    variant={"contained"}
                    color={"default"}
                    onClick={showSendEmailPreviewDialog}
                  >
                    Send preview
                  </Button>
                  <Button
                    variant={"contained"}
                    color={"primary"}
                    onClick={showSendEmailsConfirmationDialog}
                  >
                    Send emails
                  </Button>

                  {state.confirmingEmailPreview && (
                    <ConfirmDialog
                      titleText={"Send email preview?"}
                      cancelDisabled={state.sendingEmailPreview}
                      confirmDisabled={state.sendingEmailPreview}
                      disableBackdropClick={state.sendingEmailPreview}
                      confirmText={"Send"}
                      onCancel={closeSendEmailPreviewDialog}
                      onConfirm={confirmSendEmailPreviewDialog}
                    >
                      <TextField
                        required
                        disabled={state.sendingEmailPreview}
                        className={classes.field}
                        label={t("common.formFields.email")}
                        type="text"
                        value={state.previewEmailAddress ?? ""}
                        onChange={e =>
                          setState({
                            ...state,
                            previewEmailAddress: e.target.value as string
                          })
                        }
                        helperText={
                          "Note: email of the user of the app, not admin"
                        }
                      />
                    </ConfirmDialog>
                  )}

                  {state.confirmingSendEmails && (
                    <ConfirmDialog
                      titleText={"Send email to target group?"}
                      confirmText={"Yes"}
                      cancelText={"No"}
                      onCancel={closeSendEmailsConfirmationDialog}
                      onConfirm={confirmSendEmailsConfirmationDialog}
                    >
                      Are you sure you want to send emails using template{" "}
                      <b>{state.emailTemplate?.description}</b> and target group{" "}
                      <b>{targetGroupToDisplayName(state.targetGroup ?? "")}</b>
                      ?
                      {state.targetGroup === "RecipientsInStore" ? (
                        <>
                          The users in following stores will receive emails:{" "}
                          <b>{getStoreNames()}</b>.
                        </>
                      ) : null}
                    </ConfirmDialog>
                  )}
                </Card>
              )}

              {state.marketingType === "notification" && (
                <Card className={classes.section}>
                  <Button
                    disabled={!isNotificationsValid()}
                    variant={"contained"}
                    color={"primary"}
                    onClick={showSendNotificationsConfirmationDialog}
                  >
                    Send notifications
                  </Button>

                  {state.confirmingSendNotifications && (
                    <ConfirmDialog
                      titleText={"Send notifications to target group?"}
                      confirmText={"Yes"}
                      cancelText={"No"}
                      onCancel={closeSendNotificationsConfirmationDialog}
                      onConfirm={confirmSendNotificationsConfirmationDialog}
                    >
                      Are you sure you want to send notifications to target
                      group{" "}
                      <b>{targetGroupToDisplayName(state.targetGroup ?? "")}</b>
                      ?
                      {state.targetGroup === "RecipientsInStore" ? (
                        <>
                          The users in following stores will receive
                          notifications: <b>{getStoreNames()}</b>.
                        </>
                      ) : null}
                    </ConfirmDialog>
                  )}
                </Card>
              )}
            </>
          )}
        </>
      );

    case "SENDING":
      return (
        <>
          <div className={classes.loading}>
            <CircularProgress />
          </div>
          <div className={classes.loading} style={{ marginTop: "2rem" }}>
            Sending...
          </div>
        </>
      );

    case "EMAILS_SENT":
      return (
        <>
          <Alert severity={"success"} className={classes.trailing}>
            <AlertTitle>Success!</AlertTitle>
            Emails have now been queued for sending. If you want to see the
            status, look in SendGrids admin.
          </Alert>
          <Button
            variant={"contained"}
            color={"primary"}
            onClick={() => setState({ type: "MANAGE" })}
          >
            Send more
          </Button>
        </>
      );

    case "NOTIFICATIONS_SENT":
      return (
        <>
          <Alert severity={"success"} className={classes.trailing}>
            <AlertTitle>Success!</AlertTitle>
            Notifications have now been queued for sending.
          </Alert>
          <Button
            variant={"contained"}
            color={"primary"}
            onClick={() => setState({ type: "MANAGE" })}
          >
            Send more
          </Button>
        </>
      );
  }
};
